import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Briefcase, User, Users, ArrowRight, Command, Zap, Plus, LogOut, ShieldCheck, AlertTriangle, FileText, ClipboardCheck, Library } from "lucide-react";

const TYPE_META = {
  job: { icon: Briefcase, label: "Job", color: "text-pink" },
  candidate: { icon: User, label: "Candidate", color: "text-teal" },
  employee: { icon: Users, label: "Employee", color: "text-navy" },
};

// Global actions available via ">" prefix. Each is a route that opens an entity's create dialog.
const ACTIONS = [
  { id: "act-new-job", label: "Create a job", subtitle: "Post a new role and open the pipeline", icon: Plus, link: "/app/recruit/jobs?action=new", roles: ["RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-invite-candidate", label: "Invite a candidate", subtitle: "Send a video-interview invite", icon: Plus, link: "/app/recruit/jobs?action=invite", roles: ["RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-new-pool", label: "New talent pool", subtitle: "Curate a saved list of candidates", icon: Users, link: "/app/recruit/pools?action=new", roles: ["RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-new-qs", label: "New question set", subtitle: "Add to the shared library", icon: Library, link: "/app/recruit/library?action=new-qs", roles: ["RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-new-email", label: "New email template", subtitle: "Save reusable invite/reject/offer copy", icon: FileText, link: "/app/recruit/library?action=new-email", roles: ["RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-onb-template", label: "New onboarding template", subtitle: "Design a checklist for a role", icon: ClipboardCheck, link: "/app/onboarding/templates?action=new", roles: ["HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-offboarding", label: "Start offboarding", subtitle: "Kick off an exit workflow", icon: LogOut, link: "/app/offboarding?action=new", roles: ["HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-report-incident", label: "Report incident", subtitle: "H&S near-miss, injury, or property damage", icon: AlertTriangle, link: "/me/safety?action=incident", roles: ["EMPLOYEE","HIRING_MANAGER","RECRUITER","HR_ADMIN","SUPER_ADMIN"] },
  { id: "act-add-monitor", label: "Add compliance monitor", subtitle: "Custom control for your framework", icon: ShieldCheck, link: "/app/compliance-plus?action=new-monitor", roles: ["HR_ADMIN","SUPER_ADMIN"] },
];

// Global slash-commands available via "/" prefix.
// Each command has a `run(nav, toast)` function that mutates localStorage / document class / etc.
const SLASH_COMMANDS = [
  {
    id: "sl-theme-dark", label: "/theme dark", subtitle: "Switch to dark mode",
    run: (nav, toast) => { document.documentElement.classList.add("dark"); localStorage.setItem("alfred_theme", "dark"); toast.success("Dark mode on"); },
  },
  {
    id: "sl-theme-light", label: "/theme light", subtitle: "Switch to light mode",
    run: (nav, toast) => { document.documentElement.classList.remove("dark"); localStorage.setItem("alfred_theme", "light"); toast.success("Light mode on"); },
  },
  {
    id: "sl-theme-system", label: "/theme system", subtitle: "Follow OS preference",
    run: (nav, toast) => {
      localStorage.removeItem("alfred_theme");
      const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", prefersDark);
      toast.success("Theme follows your device");
    },
  },
  {
    id: "sl-notify-off", label: "/notify off", subtitle: "Silence all notifications",
    run: (nav, toast) => { localStorage.setItem("alfred_notify", "off"); toast.success("Notifications silenced"); },
  },
  {
    id: "sl-notify-on", label: "/notify on", subtitle: "Re-enable notifications",
    run: (nav, toast) => { localStorage.removeItem("alfred_notify"); toast.success("Notifications on"); },
  },
  {
    id: "sl-tz-sydney", label: "/timezone Sydney", subtitle: "Display times in AEST/AEDT",
    run: (nav, toast) => { localStorage.setItem("alfred_tz", "Australia/Sydney"); toast.success("Times will show in Sydney"); },
  },
  {
    id: "sl-tz-nyc", label: "/timezone New York", subtitle: "Display times in ET",
    run: (nav, toast) => { localStorage.setItem("alfred_tz", "America/New_York"); toast.success("Times will show in New York"); },
  },
  {
    id: "sl-tz-london", label: "/timezone London", subtitle: "Display times in GMT/BST",
    run: (nav, toast) => { localStorage.setItem("alfred_tz", "Europe/London"); toast.success("Times will show in London"); },
  },
  {
    id: "sl-tz-auto", label: "/timezone auto", subtitle: "Follow your browser's timezone",
    run: (nav, toast) => { localStorage.removeItem("alfred_tz"); toast.success("Times follow your browser"); },
  },
  {
    id: "sl-go-inbox", label: "/go inbox", subtitle: "Jump to notifications inbox",
    run: (nav) => nav("/app/notifications"),
  },
  {
    id: "sl-go-me", label: "/go me", subtitle: "Jump to your Employee portal",
    run: (nav) => nav("/me"),
  },
  {
    id: "sl-logout", label: "/logout", subtitle: "Sign out of alfredHR",
    run: (nav, toast) => { toast.info("Signing you out…"); setTimeout(() => window.location.href = "/api/auth/logout", 300); },
  },
];

import { useAuth } from "@/lib/auth";

export default function CommandPalette({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const isActionMode = q.startsWith(">");
  const isSlashMode = q.startsWith("/");
  const actionQuery = isActionMode ? q.slice(1).trim().toLowerCase() : "";
  const slashQuery = isSlashMode ? q.slice(1).trim().toLowerCase() : "";

  // Filter global actions by role + query
  const userRole = user?.role || "EMPLOYEE";
  const actionResults = isActionMode
    ? ACTIONS.filter(a => a.roles.includes(userRole))
        .filter(a => !actionQuery || a.label.toLowerCase().includes(actionQuery) || a.subtitle.toLowerCase().includes(actionQuery))
    : [];
  const slashResults = isSlashMode
    ? SLASH_COMMANDS.filter(c => !slashQuery || c.label.toLowerCase().includes(slashQuery) || c.subtitle.toLowerCase().includes(slashQuery))
    : [];

  // Run search (debounced) — skip when in action or slash mode
  useEffect(() => {
    if (!open) return;
    if (isActionMode || isSlashMode) { setResults([]); setActive(0); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
        setActive(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 180);
  }, [q, open, isActionMode, isSlashMode]);

  // Reset when closed
  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setActive(0); }
    else {
      setTimeout(() => inputRef.current?.focus(), 50);
      // Load recents from localStorage
      try {
        const raw = localStorage.getItem("cmdk_recents");
        setRecents(raw ? JSON.parse(raw) : []);
      } catch { setRecents([]); }
    }
  }, [open]);

  const openResult = useCallback((r) => {
    if (r?.link) {
      // Persist to localStorage recents (dedupe + trim to 5)
      try {
        const raw = localStorage.getItem("cmdk_recents");
        const list = raw ? JSON.parse(raw) : [];
        const filtered = list.filter(x => x.id !== r.id);
        filtered.unshift({ id: r.id, type: r.type, title: r.title, subtitle: r.subtitle, link: r.link });
        localStorage.setItem("cmdk_recents", JSON.stringify(filtered.slice(0, 5)));
      } catch {}
      nav(r.link); onOpenChange(false);
    }
  }, [nav, onOpenChange]);

  const runAction = useCallback((a) => {
    if (a?.link) { nav(a.link); onOpenChange(false); }
  }, [nav, onOpenChange]);

  const runSlash = useCallback((cmd) => {
    if (!cmd) return;
    try { cmd.run(nav, toast); } catch (e) { toast.error("Command failed"); }
    onOpenChange(false);
  }, [nav, onOpenChange]);

  const onKey = (e) => {
    const currentList = isActionMode ? actionResults : isSlashMode ? slashResults : results;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, currentList.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (isActionMode) runAction(actionResults[active]);
      else if (isSlashMode) runSlash(slashResults[active]);
      else openResult(results[active]);
    }
  };

  // Group by type
  const grouped = results.reduce((acc, r) => { (acc[r.type] ||= []).push(r); return acc; }, {});
  let flatIdx = -1; // for indexed active state across groups

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 top-[15%] translate-y-0" data-testid="command-palette">
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="w-4 h-4 text-neutral-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={isActionMode ? "Run an action — e.g. 'create a job'" : isSlashMode ? "Settings command — e.g. 'theme dark'" : "Search jobs, candidates, employees…   (type '>' for actions, '/' for settings)"}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-400"
            data-testid="cmdk-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-neutral-200 text-neutral-500">
            <Command className="w-2.5 h-2.5" />K
          </kbd>
        </div>

        <div className="max-h-96 overflow-y-auto" data-testid="cmdk-results">
          {/* Action mode */}
          {isActionMode && (
            <div data-testid="cmdk-actions">
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-pink font-semibold flex items-center gap-1">
                <Zap className="w-3 h-3" /> Actions{actionQuery ? ` · matching "${actionQuery}"` : ""}
              </div>
              {actionResults.length === 0 && (
                <div className="p-6 text-center text-sm text-neutral-500" data-testid="cmdk-actions-empty">
                  No matching action. Try typing part of "create", "invite", or "start".
                </div>
              )}
              {actionResults.map((a, i) => {
                const Icon = a.icon;
                const isActive = i === active;
                return (
                  <button
                    key={a.id}
                    onClick={() => runAction(a)}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${isActive ? "bg-pink/5" : "hover:bg-neutral-50"}`}
                    data-testid={`cmdk-action-${a.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-pink/10 grid place-items-center shrink-0">
                      <Icon className="w-4 h-4 text-pink" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{a.label}</div>
                      <div className="text-xs text-neutral-500 truncate">{a.subtitle}</div>
                    </div>
                    {isActive && <ArrowRight className="w-3.5 h-3.5 text-pink shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
          {/* Slash mode */}
          {isSlashMode && (
            <div data-testid="cmdk-slash">
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-teal font-semibold flex items-center gap-1">
                <Command className="w-3 h-3" /> Settings command{slashQuery ? ` · matching "${slashQuery}"` : ""}
              </div>
              {slashResults.length === 0 && (
                <div className="p-6 text-center text-sm text-neutral-500" data-testid="cmdk-slash-empty">
                  No matching command. Try "theme dark", "notify off", or "timezone Sydney".
                </div>
              )}
              {slashResults.map((c, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={c.id}
                    onClick={() => runSlash(c)}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${isActive ? "bg-teal/5" : "hover:bg-neutral-50"}`}
                    data-testid={`cmdk-slash-${c.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-teal/10 grid place-items-center shrink-0">
                      <span className="text-teal text-xs font-mono font-bold">/</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-mono font-semibold truncate">{c.label}</div>
                      <div className="text-xs text-neutral-500 truncate">{c.subtitle}</div>
                    </div>
                    {isActive && <ArrowRight className="w-3.5 h-3.5 text-teal shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
          {!isActionMode && !isSlashMode && loading && <div className="p-6 text-center text-sm text-neutral-500">Searching…</div>}
          {!isActionMode && !isSlashMode && !loading && q.length >= 2 && results.length === 0 && (
            <div className="p-8 text-center text-sm text-neutral-500" data-testid="cmdk-empty">
              No matches for <span className="font-mono">"{q}"</span>.
            </div>
          )}
          {!isActionMode && !isSlashMode && !loading && q.length < 2 && (
            recents.length > 0 ? (
              <div data-testid="cmdk-recents">
                <div className="flex items-center justify-between px-4 pt-3 pb-1">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Jump back to</div>
                  <button
                    onClick={() => { localStorage.removeItem("cmdk_recents"); setRecents([]); }}
                    className="text-[10px] text-neutral-400 hover:text-pink"
                    data-testid="cmdk-clear-recents">
                    Clear
                  </button>
                </div>
                {recents.map(r => {
                  const meta = TYPE_META[r.type] || TYPE_META.job;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={r.id}
                      onClick={() => openResult(r)}
                      className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-neutral-50"
                      data-testid={`cmdk-recent-${r.id}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.title}</div>
                        <div className="text-xs text-neutral-500 truncate">{r.subtitle}</div>
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
                    </button>
                  );
                })}
                <div className="px-4 py-2 text-[10px] text-neutral-400 text-center border-t">
                  Start typing to search — or press <span className="font-mono bg-pink/10 text-pink px-1.5 py-0.5 rounded">&gt;</span> for actions.
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-neutral-500">
                <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2">Quick nav</div>
                <div className="text-xs">Start typing to search across your workspace.</div>
                <div className="text-[10px] text-neutral-400 mt-3">↑↓ to navigate · Enter to open · Esc to close</div>
                <div className="text-[10px] text-pink mt-2 font-semibold">
                  <span className="font-mono bg-pink/10 px-1.5 py-0.5 rounded">&gt;</span> for actions ·
                  <span className="font-mono bg-teal/10 text-teal px-1.5 py-0.5 rounded ml-1">/</span> for settings
                </div>
              </div>
            )
          )}
          {!isActionMode && !isSlashMode && Object.entries(grouped).map(([type, list]) => {
            const meta = TYPE_META[type] || TYPE_META.job;
            const Icon = meta.icon;
            return (
              <div key={type}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold px-4 pt-3 pb-1">{meta.label}s</div>
                {list.map(r => {
                  flatIdx++;
                  const isActive = results[active]?.id === r.id;
                  return (
                    <button
                      key={r.id}
                      onClick={() => openResult(r)}
                      onMouseEnter={() => setActive(results.findIndex(x => x.id === r.id))}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${isActive ? "bg-pink/5" : "hover:bg-neutral-50"}`}
                      data-testid={`cmdk-result-${r.id}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.title}</div>
                        <div className="text-xs text-neutral-500 truncate">{r.subtitle}</div>
                      </div>
                      {isActive && <ArrowRight className="w-3.5 h-3.5 text-pink shrink-0" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
